import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Server-driven table state: debounced search, page/pageSize, stale-response guard.
 *
 * @param {object} options
 * @param {boolean} [options.enabled=true]
 * @param {string} [options.search='']
 * @param {number} [options.initialPageSize=10]
 * @param {number} [options.debounceMs=300]
 * @param {(params: { page: number, pageSize: number, search: string }) => Promise<{ items: unknown[], total: number }>} options.fetcher
 */
export function useServerTable({
  enabled = true,
  search = '',
  initialPageSize = 10,
  debounceMs = 300,
  fetcher,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(() => String(search || '').trim());

  const fetchGen = useRef(0);
  const debounceRef = useRef(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = String(searchRef.current || '').trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, debounceMs]);

  const load = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    const gen = ++fetchGen.current;
    setLoading(true);
    try {
      const result = await fetcher({ page, pageSize, search: debouncedSearch });
      if (gen !== fetchGen.current) return;
      setRows(Array.isArray(result?.items) ? result.items : []);
      setTotal(Number(result?.total) || 0);
    } catch (e) {
      if (gen !== fetchGen.current) return;
      console.error('useServerTable fetch failed:', e);
      setRows([]);
      setTotal(0);
      throw e;
    } finally {
      if (gen === fetchGen.current) setLoading(false);
    }
  }, [enabled, page, pageSize, debouncedSearch, fetcher]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handlePageChange = useCallback((nextPage, nextPageSize) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  }, [pageSize]);

  const refresh = useCallback(() => {
    load().catch(() => {});
  }, [load]);

  return {
    page,
    pageSize,
    rows,
    total,
    loading,
    setPage,
    setPageSize,
    handlePageChange,
    refresh,
  };
}
